import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import EmailPageShell from './EmailPageShell';
import RequestLinkForm from './RequestLinkForm';
import PreferencesPanel from './PreferencesPanel';
import { fetchPreferencesByToken, updatePreferencesByToken } from './emailPreferencesApi';
import styles from './Email.module.css';

const EmailPreferencesPage = () => {
  const [params] = useSearchParams();
  const t = params.get('t') || '';

  const [view, setView] = useState(t ? 'loading' : 'request');
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!t) return;
    setView('loading');
    const res = await fetchPreferencesByToken(t, 'preferences');
    if (!res.ok) {
      setError(res.status === 400 ? res.error : 'We could not load your preferences. Please try again.');
      setView(res.status === 400 ? 'request' : 'error');
      return;
    }
    setInfo(res.data);
    setView('ready');
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const save = async (categories) => {
    const res = await updatePreferencesByToken(t, categories);
    if (res.ok) setInfo(res.data);
    return res;
  };

  const signedIn = typeof window !== 'undefined' && Boolean(window.localStorage?.getItem('token'));

  let content;
  if (view === 'loading') {
    content = <div className={styles.loader} role="status">Loading your preferences…</div>;
  } else if (view === 'error') {
    content = (
      <>
        <h1 className={styles.title}>Something went wrong</h1>
        <p className={styles.error} role="alert">{error}</p>
        <div className={styles.actions}>
          <button type="button" className={styles.primaryBtn} onClick={load}>Try again</button>
        </div>
      </>
    );
  } else if (view === 'request') {
    content = (
      <>
        <h1 className={styles.title}>Manage your email preferences</h1>
        {error && <p className={styles.error} role="alert">{error}</p>}
        <p className={styles.subtitle}>
          Enter the email address you receive Bubble Ai emails at and we'll send you a secure link to choose which emails you get —
          no password needed.
        </p>
        <RequestLinkForm />
        {signedIn && (
          <p className={styles.note}>
            Signed in? You can also manage email notifications from your <Link to="/user/dashboard/profile">profile</Link>.
          </p>
        )}
      </>
    );
  } else if (view === 'ready' && info) {
    content = (
      <>
        <h1 className={styles.title}>Email preferences</h1>
        <p className={styles.subtitle}>
          Choose which emails <span className={styles.email}>{info.maskedEmail}</span> receives from Bubble Ai.
        </p>
        {info.suppressed && (
          <p className={styles.error} role="alert">
            Emails to this address are currently paused because earlier messages could not be delivered. Contact support if you'd like
            them turned back on.
          </p>
        )}
        <PreferencesPanel
          categoryMeta={info.categoryMeta}
          categories={info.categories}
          canIncrease={info.canResubscribe}
          onSave={save}
        />
      </>
    );
  }

  return <EmailPageShell title="Email preferences">{content}</EmailPageShell>;
};

export default EmailPreferencesPage;
